import { Component, OnInit, OnDestroy } from '@angular/core';
import {
    FormGroup,
    Validators,
    FormBuilder,
    ValidationErrors,
    FormControl,
    FormGroupDirective,
    NgForm
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ErrorStateMatcher } from '@angular/material/core';
import { Subscription } from 'rxjs';
import moment from 'moment';

import { AuthService } from '@services/auth.service';
import { UiService } from '@services/ui.service';
import { UserService } from '@services/user.service';


class ConfirmValidMatcher implements ErrorStateMatcher {
    isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        return control.touched && form.hasError('mismatch');
    }
}

@Component({
    selector: 'app-change-password',
    templateUrl: './change-password.component.html',
    styleUrls: ['./change-password.component.scss']
})
export class ChangePasswordComponent implements OnInit, OnDestroy {
    private loadingSub: Subscription;

    passwordForm: FormGroup;
    isWelcome: boolean;
    isExpired: boolean;
    isLoading: boolean;
    isNewUser: boolean;

    confirmValidMatcher = new ConfirmValidMatcher();

    token: string;
    id: string;

    constructor(
        private authSrv: AuthService,
        private fb: FormBuilder,
        private uiSrv: UiService,
        private route: ActivatedRoute,
        private router: Router,
        private userSrv: UserService
    ) { }

    ngOnInit() {
        this.checkRouteParams();
        this.initForm();
        this.initSubscriptions();
    }

    onSubmit(): void {
        this.isLoading = true;

        if(this.isWelcome) {
            var newPassword: string = this.passwordForm.value.newPassword;
            var isSubscribedNewsletter: boolean = null;
            if (this.isNewUser) isSubscribedNewsletter = this.passwordForm.value.isSubscribedNewsletter;

            this.authSrv.resetPassword(this.id, this.token, newPassword, isSubscribedNewsletter).subscribe();
        } else {
            this.authSrv.updatePassword(this.passwordForm.value).subscribe();
        }
    }

    private checkRouteParams(): void {
        this.token = this.route.snapshot.queryParams["token"];
        this.id = this.route.snapshot.queryParams["id"];

        const exp = this.route.snapshot.queryParams["exp"];
        const username = this.route.snapshot.queryParams["user"];
        const expDate = moment(exp, "DD-MM-yy hh:mm:ss TZD");
        const now = moment();

        this.isNewUser = this.router.url.includes('/welcome?');

        if(this.token && this.id && exp && username) {
            // Logout if user is already logged in with another account to avoid confusion
            if(this.authSrv.getDecodedToken()) {
                localStorage.clear();
                this.authSrv.authChanged.next(false);
            }

            if(expDate < now) {
                this.isExpired = true;
                this.authSrv.requestPasswordInit(username).subscribe();
            } else {
                this.isWelcome = true;
            }
        }
    }

    private initForm(): void {
        this.passwordForm = this.fb.group({
            newPassword: ['', [Validators.required, Validators.minLength(5)]],
            confirmPassword: ['', Validators.required],
            isSubscribedNewsletter: [false]
        }, { validators: this.passwordMatchValidator });

        if(!this.isWelcome) {
            this.passwordForm.addControl('currentPassword', this.fb.control('', [Validators.required]));
        }
    }

    // Check if password and confirmPassword matches
    private passwordMatchValidator(control: FormGroup): ValidationErrors | null {
        const newPassword = control.get('newPassword');
        const confirmPassword = control.get('confirmPassword');

        return newPassword.value !== confirmPassword.value ? { 'mismatch': true } : null;
    }

    private initSubscriptions(): void {
        this.isLoading = false;

        this.loadingSub = this.uiSrv.loadingStateChanged
            .subscribe(loadingState => {
                this.isLoading = loadingState;
            });
    }

    ngOnDestroy() {
        if(this.loadingSub) {
            this.loadingSub.unsubscribe();
        }
    }
}
